import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { ClockIcon } from '@heroicons/react/24/outline';
import quoteService from '../../../services/quote.service';
import type {
  QuoteDetailData, TariffRateRow, StopoffRow, ProductRateRow, HandlingItemRow, DeliveryLaneRow,
} from '../fixtures';

interface Props {
  open: boolean;
  quoteNumber: number;
  /** The revision currently open in the detail page — its column is marked. */
  currentRevision?: number;
  onClose: () => void;
  /** Navigate to a chosen revision (column header / "Open" click). */
  onSelectRevision?: (revisionNumber: number) => void;
}

/** A single comparable row: a label and a getter that renders one revision's
 *  value as a display string. Change detection is a plain string compare of
 *  adjacent columns, so list-valued fields serialize to newline-joined text. */
type FieldRow = {
  label: string;
  get: (r: QuoteDetailData) => string;
  /** Charge scope for per-charge diffing in "Changes only" mode (Additional
   *  Charges). When set, "Changes only" shows just the charges that VARY across
   *  revisions instead of the whole list. */
  diffScope?: string;
};

const s = (v: unknown): string => (v == null ? '' : String(v));

const slashPair = (a: unknown, b: unknown): string => {
  const l = s(a), r = s(b);
  return l || r ? `${l} / ${r}` : '';
};

const cityStateZip = (city: unknown, state: unknown, zip: unknown): string => {
  const c = s(city), st = s(state), z = s(zip);
  if (!c && !st && !z) return '';
  return `${c}${c && st ? ', ' : ''}${st} ${z}`.trim();
};

const stopoffsStr = (rows?: StopoffRow[]): string =>
  (rows ?? [])
    .map((r) => `${r.stopNum}. ${r.stopName} — ${cityStateZip(r.city, r.state, r.zip)} — ${r.stopType}`)
    .join('\n');

const chargesStr = (rows: TariffRateRow[] | undefined, scope: string): string =>
  (rows ?? [])
    .filter((r) => r.scope === scope)
    .map((r) => `${r.description} / $${r.rate} / ${r.rateType}`)
    .join('\n');

const productRatesStr = (rows?: ProductRateRow[]): string =>
  (rows ?? [])
    .map((r) => `${r.sku} ${r.description} — store ${r.storageRate} ${r.storageType} / handle ${r.handlingRate} ${r.handlingType}`)
    .join('\n');

const handlingStr = (rows: HandlingItemRow[] | undefined, type: string): string =>
  (rows ?? [])
    .filter((r) => r.handlingType === type)
    .map((r) => `${r.description} / ${r.rate} ${r.unitType} / ${r.rateType}`)
    .join('\n');

const lanesStr = (rows?: DeliveryLaneRow[]): string =>
  (rows ?? [])
    .map((l) => `${l.warehouse} — ${l.equipType} — ${l.deliverToName} ${cityStateZip(l.city, l.state, l.zip)} / rate ${l.deliveryRate}`)
    .join('\n');

// Lifecycle/revision fields shown for every quote type (V1 viewrevisionhistory
// quoterevision block, inline_quote.pl:3722-3730).
const SHARED_ROWS: FieldRow[] = [
  { label: 'Quoted By', get: (r) => r.quotedBy },
  { label: 'Sold By', get: (r) => s(r.soldBy) },
  { label: 'Status', get: (r) => r.rawStatus || r.status },
  { label: 'Secured Date', get: (r) => r.statusDate },
  { label: 'Revision Reason', get: (r) => r.revReason },
  { label: 'Volume', get: (r) => r.volume },
  { label: 'Effective Date', get: (r) => r.effective },
  { label: 'End Date', get: (r) => r.endDate },
  { label: 'Last Used', get: (r) => r.lastUsed },
];

// DIRECT-specific rows (directquote + its stopoffs/charges).
const DIRECT_ROWS: FieldRow[] = [
  { label: 'Move Type', get: (r) => s(r.direct?.moveType) },
  { label: 'Size / Type', get: (r) => `${s(r.direct?.size)} ${s(r.direct?.equipType)}`.trim() },
  { label: 'Commodity', get: (r) => s(r.direct?.commodity) },
  { label: 'Lbs / Kgs', get: (r) => slashPair(r.direct?.lbs, r.direct?.kgs) },
  { label: 'Legal', get: (r) => s(r.direct?.legalWeight) },
  { label: 'Overweight', get: (r) => s(r.direct?.overweight) },
  { label: 'OOG', get: (r) => s(r.direct?.oog) },
  { label: 'Chassis Type', get: (r) => s(r.direct?.chassisType) },
  { label: 'Length', get: (r) => s(r.direct?.lengthInches) },
  { label: 'Width', get: (r) => s(r.direct?.widthInches) },
  { label: 'Height', get: (r) => s(r.direct?.heightInches) },
  { label: 'Triaxle', get: (r) => s(r.direct?.triaxle) },
  { label: 'Swing', get: (r) => s(r.direct?.swing) },
  { label: 'Hazmat', get: (r) => s(r.direct?.hazmat) },
  { label: 'Rate Zone', get: (r) => s(r.direct?.rateZone) },
  { label: 'Delivery Type', get: (r) => s(r.direct?.deliveryZone) },
  { label: 'Delivery Location', get: (r) => s(r.direct?.deliverToName) },
  { label: 'Address 1', get: (r) => s(r.direct?.deliverToAddr1) },
  { label: 'City/State/Zip', get: (r) => cityStateZip(r.direct?.deliverToCity, r.direct?.deliverToState, r.direct?.deliverToZip) },
  { label: 'Dray', get: (r) => s(r.direct?.drayRate) },
  { label: 'Total with Charges', get: (r) => s(r.direct?.drayWithCharges) },
  { label: 'FSC Type', get: (r) => s(r.direct?.fscType) },
  { label: 'FSC', get: (r) => s(r.direct?.fscRate) },
  { label: 'Cust Tariff #', get: (r) => s(r.direct?.fscTariff) },
  { label: 'Comments', get: (r) => s(r.direct?.comments) },
  { label: 'Stopoffs', get: (r) => stopoffsStr(r.directStopoffs) },
  { label: 'Additional Charges', get: (r) => chargesStr(r.tariffRates, 'directquote'), diffScope: 'directquote' },
];

/**
 * Per-charge diff across the revision set (Additional Charges, "Changes only").
 * Returns one cell string per revision containing ONLY the charges whose
 * "$rate / type" varies across revisions (present-in-some counts as a change) —
 * matching V1, which shows just the changed charges rather than the full list.
 */
function chargesDiffValues(revisions: QuoteDetailData[], scope: string): string[] {
  const perRev = revisions.map((rev) => {
    const m = new Map<string, string>();
    (rev.tariffRates ?? []).filter((r) => r.scope === scope)
      .forEach((r) => m.set(r.description, `$${r.rate} / ${r.rateType}`));
    return m;
  });
  const allDescs = new Set<string>();
  perRev.forEach((m) => m.forEach((_v, d) => allDescs.add(d)));
  // Keep descriptions whose value (or presence) differs across revisions.
  const varying = [...allDescs].filter((d) => {
    const vals = perRev.map((m) => m.get(d) ?? ''); // '' = absent on that revision
    return vals.some((v) => v !== vals[0]);
  });
  return perRev.map((m) =>
    varying.filter((d) => m.has(d)).map((d) => `${d} / ${m.get(d)}`).join('\n'),
  );
}

// WAREHOUSE-specific rows (transloadquote + handling/product rates/lanes).
const WAREHOUSE_ROWS: FieldRow[] = [
  { label: 'Move Type', get: (r) => s(r.warehouse?.moveType) },
  { label: 'Whse Type', get: (r) => s(r.warehouse?.whseType) },
  { label: 'Rate Zone', get: (r) => s(r.warehouse?.rateZone) },
  { label: 'Commodity', get: (r) => s(r.warehouse?.commodity) },
  { label: 'Hazmat', get: (r) => s(r.warehouse?.hazmat) },
  { label: 'Package Type', get: (r) => s(r.warehouse?.packageType) },
  { label: '# of Units', get: (r) => s(r.warehouse?.numUnits) },
  { label: 'Size / Type', get: (r) => `${s(r.warehouse?.size)} ${s(r.warehouse?.equipType)}`.trim() },
  { label: 'OOG', get: (r) => s(r.warehouse?.oog) },
  { label: 'Chassis Type', get: (r) => s(r.warehouse?.chassisType) },
  { label: 'Length', get: (r) => s(r.warehouse?.lengthInches) },
  { label: 'Width', get: (r) => s(r.warehouse?.widthInches) },
  { label: 'Height', get: (r) => s(r.warehouse?.heightInches) },
  { label: 'Lbs / Kgs', get: (r) => slashPair(r.warehouse?.lbs, r.warehouse?.kgs) },
  { label: 'Legal', get: (r) => s(r.warehouse?.legalWeight) },
  { label: 'Overweight', get: (r) => s(r.warehouse?.overweight) },
  { label: '# Included Overflow', get: (r) => s(r.warehouse?.numOverflow) },
  { label: 'Triaxle', get: (r) => s(r.warehouse?.triaxle) },
  { label: 'Swing', get: (r) => s(r.warehouse?.swing) },
  { label: 'Warehouse', get: (r) => s(r.warehouse?.warehouse) },
  { label: 'Drayage', get: (r) => s(r.warehouse?.drayRate) },
  { label: 'Total with Charges', get: (r) => s(r.warehouse?.drayWithCharges) },
  { label: 'FSC', get: (r) => s(r.warehouse?.fuelSurcharge) },
  { label: 'FSC Type', get: (r) => s(r.warehouse?.fuelType) },
  { label: 'Cust Tariff #', get: (r) => s(r.warehouse?.fuelTariff) },
  { label: 'Storage Type', get: (r) => s(r.warehouse?.storageType) },
  { label: 'Storage Rate', get: (r) => s(r.warehouse?.storageRate) },
  { label: 'Comments', get: (r) => s(r.warehouse?.comments) },
  { label: 'Product Rates', get: (r) => productRatesStr(r.warehouse?.productRates) },
  { label: 'Handling In', get: (r) => handlingStr(r.handlingItems, 'handlingin') },
  { label: 'Handling Out', get: (r) => handlingStr(r.handlingItems, 'handlingout') },
  { label: 'Delivery Lanes', get: (r) => lanesStr(r.deliveryLanes) },
  { label: 'Additional Charges', get: (r) => chargesStr(r.tariffRates, 'transloadquote') },
];

const rowsForType = (quoteType: string): FieldRow[] =>
  [...SHARED_ROWS, ...(quoteType === 'WAREHOUSE' ? WAREHOUSE_ROWS : DIRECT_ROWS)];

/**
 * Revision History compare view (V1 inline_quote.pl viewrevisionhistory). Shows
 * every revision of a quote side by side — fields down the left, one column per
 * revision — and highlights any cell whose value changed from the revision to
 * its left. "Changes only" hides rows that never change across the set, matching
 * V1's changed-fields-only stacked layout. Click a column to open that revision.
 */
const RevisionHistoryModal: React.FC<Props> = ({
  open, quoteNumber, currentRevision, onClose, onSelectRevision,
}) => {
  const [revisions, setRevisions] = useState<QuoteDetailData[]>([]);
  const [quoteType, setQuoteType] = useState('DIRECT');
  const [totalRevisions, setTotalRevisions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changesOnly, setChangesOnly] = useState(false);

  // Esc closes; lock body scroll while open (app modal convention).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !quoteNumber) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    quoteService.getQuoteRevisions(quoteNumber)
      .then((data) => {
        if (cancelled) return;
        setRevisions(data.revisions);
        setQuoteType(data.quoteType || 'DIRECT');
        setTotalRevisions(data.totalRevisions ?? data.revisions.length);
      })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, quoteNumber]);

  const fieldRows = useMemo(() => rowsForType(quoteType), [quoteType]);

  // Pre-compute each row's per-revision values + whether it changed anywhere,
  // so render is a plain lookup and "Changes only" can filter cheaply.
  const computed = useMemo(() => fieldRows.map((row) => {
    const full = revisions.map((rev) => row.get(rev));
    // Change detection always runs on the full value, so the row appears iff the
    // charges actually changed. In "Changes only" mode a diffScope row collapses
    // to just the varying charges (the whole list otherwise).
    const values = row.diffScope && changesOnly ? chargesDiffValues(revisions, row.diffScope) : full;
    const changedAt = full.map((v, i) => i > 0 && v !== full[i - 1]);
    return { label: row.label, values, changedAt, hasChange: changedAt.some(Boolean) };
  }), [fieldRows, revisions, changesOnly]);

  const visibleRows = changesOnly ? computed.filter((r) => r.hasChange) : computed;

  // Read-only revision diff: Alt+C closes; the layer keeps Alt+S off the quote
  // editor drawer underneath.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  // Portal to <body> so the overlay escapes the drawer panel's CSS transform
  // (which would otherwise be its containing block AND a lower stacking context,
  // confining + hiding it behind the drawer). At <body> its fixed positioning
  // is viewport-relative and z-[120] sits above the drawer (z-[100]).
  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-slate-900/40 flex items-center justify-center p-6"
      role="dialog"
      aria-label="Revision History"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-[1100px] max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <ModalHeader
          title={
            <span className="inline-flex items-center gap-1.5">
              <ClockIcon className="h-4 w-4 text-blue-300" />
              Revision History — Quote {quoteNumber}
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          actions={
            <label className="flex items-center gap-1.5 text-[0.71875rem] text-slate-200 cursor-pointer select-none">
              <input type="checkbox" checked={changesOnly} onChange={(e) => setChangesOnly(e.target.checked)} />
              Changes only
            </label>
          }
        />

        {/* Body */}
        <div className="overflow-auto">
          {loading && (
            <div className="px-6 py-12 text-center text-slate-400 text-[0.78125rem]">Loading revisions…</div>
          )}
          {!loading && error && (
            <div className="px-6 py-8 text-center text-red-600 text-[0.78125rem]">Failed to load revisions: {error}</div>
          )}
          {!loading && !error && revisions.length === 0 && (
            <div className="px-6 py-12 text-center text-slate-400 text-[0.8125rem]">No revisions found.</div>
          )}
          {!loading && !error && totalRevisions > revisions.length && (
            <div className="px-3 py-1.5 text-[0.6875rem] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
              Showing the most recent {revisions.length} of {totalRevisions} revisions.
            </div>
          )}
          {!loading && !error && revisions.length > 0 && (
            <table className="border-collapse text-[0.75rem]">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-100 dark:bg-gray-800 px-3 py-2 text-left text-[0.65625rem] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider border-b border-r border-slate-200 dark:border-gray-700 min-w-[150px]">
                    Field
                  </th>
                  {revisions.map((rev) => {
                    const isCurrent = rev.revisionNumber === currentRevision;
                    return (
                      <th
                        key={rev.revisionNumber}
                        className={[
                          'px-3 py-2 text-left border-b border-slate-200 dark:border-gray-700 min-w-[180px] align-top',
                          isCurrent ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-slate-100 dark:bg-gray-800',
                        ].join(' ')}
                      >
                        <button
                          type="button"
                          onClick={() => rev.revisionNumber != null && onSelectRevision?.(rev.revisionNumber)}
                          className="text-[0.75rem] font-bold text-blue-600 hover:underline"
                          title="Open this revision"
                        >
                          Rev {rev.revisionNumber}{isCurrent ? ' (Currently Viewing)' : ''}
                        </button>
                        <div className="text-[0.65625rem] font-normal text-slate-400 dark:text-gray-500">{rev.createDate || '—'}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.label} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 px-3 py-1.5 font-medium text-slate-500 dark:text-gray-400 border-b border-r border-slate-100 dark:border-gray-800 whitespace-nowrap align-top">
                      {row.label}
                    </td>
                    {row.values.map((val, i) => (
                      <td
                        key={i}
                        className={[
                          'px-3 py-1.5 border-b border-slate-100 dark:border-gray-800 align-top whitespace-pre-line',
                          row.changedAt[i]
                            ? 'bg-amber-50 dark:bg-amber-900/30 font-semibold text-slate-900 dark:text-gray-100'
                            : 'text-slate-700 dark:text-gray-300',
                        ].join(' ')}
                      >
                        {val || <span className="text-slate-300 dark:text-gray-600">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={revisions.length + 1} className="px-6 py-10 text-center text-slate-400 text-[0.78125rem]">
                      No changes between revisions.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-slate-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-[0.65625rem] text-slate-400">
            <span className="inline-block w-2.5 h-2.5 align-middle bg-amber-200 dark:bg-amber-800 rounded-sm mr-1" />
            highlighted = changed from previous revision
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[0.75rem] text-slate-600 dark:text-gray-300 bg-panel border border-slate-300 dark:border-gray-600 rounded hover:bg-slate-50 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RevisionHistoryModal;
